/**
 * Fable Worlds registry — the ONE mount system.
 *
 * Glob-driven: every directory under `src/worlds/<slug>/` that exports `meta` from
 * `world.meta.ts` and a default component from `index.tsx` is reachable full-screen at
 *
 *     /w/<slug>          History-API path (the dev server, `vite preview` and the
 *                        dist server all fall back to index.html, so deep paths work)
 *
 * No world is named in this file, so a builder never edits it to add one.
 *
 * The wave-1 builders each shipped their own mount path (`#/world/<slug>`, `#/w/<slug>`,
 * `/worlds/<slug>`, per-world HTML entries). Those are gone; the hash forms are kept alive
 * only as redirects into `/w/<slug>` so any link already published keeps working.
 */
import type { ComponentType } from "react";

export type WorldMeta = {
  id: string;
  name: string;
  slug: string;
  title: string;
  description: string;
};

type MetaModule = { meta?: WorldMeta; default?: WorldMeta };
type PageModule = { default: ComponentType };

const metaModules = import.meta.glob<MetaModule>("./*/world.meta.ts", { eager: true });
const pageModules = import.meta.glob<PageModule>("./*/index.tsx");

export type WorldEntry = {
  meta: WorldMeta;
  load: () => Promise<PageModule>;
};

function dirOf(path: string) {
  return path.split("/")[1] ?? "";
}

/**
 * Worlds that have a world.meta.ts but could not be registered from it.
 *
 * This exists because a silent drop already cost a full verification run: W1-5's two
 * worlds exported their meta as `worldMeta`/default only, the glob read `mod.meta`,
 * found undefined, and quietly filtered both out — so /w/chalkline and /w/nightsignal
 * rendered the showcase shell instead, and the specs failed far downstream on symptoms.
 * A world that cannot be registered is now loud, and never merely absent.
 */
export const REGISTRY_ERRORS: string[] = [];

export const WORLDS: Record<string, WorldEntry> = Object.fromEntries(
  Object.entries(metaModules)
    .map(([path, mod]) => {
      const dir = dirOf(path);
      const loader = pageModules[`./${dir}/index.tsx`];
      // Accept the named export (the contract) or the default (what two builders shipped).
      const meta = mod.meta ?? mod.default;
      if (!meta) {
        REGISTRY_ERRORS.push(`${path}: exports no \`meta\` and no default — world not registered`);
        return null;
      }
      if (!loader) {
        REGISTRY_ERRORS.push(`./${dir}/index.tsx is missing — world "${meta.slug}" not registered`);
        return null;
      }
      if (meta.slug !== dir) {
        REGISTRY_ERRORS.push(`${path}: meta.slug "${meta.slug}" does not match its directory "${dir}"`);
      }
      return [meta.slug, { meta, load: loader } satisfies WorldEntry];
    })
    .filter((entry): entry is [string, WorldEntry] => entry !== null)
);

if (REGISTRY_ERRORS.length) {
  // Surfaced as a console error on every page: the worlds specs assert zero console
  // errors, so a malformed world fails the suite at its cause instead of its symptom.
  console.error(`[worlds/registry] ${REGISTRY_ERRORS.length} world(s) not registered:\n  ${REGISTRY_ERRORS.join("\n  ")}`);
}

/** Every built world, id-sorted (FW-01, FW-06, …). Used by the showcase and the sitemap. */
export const WORLD_METAS: WorldMeta[] = Object.values(WORLDS)
  .map((entry) => entry.meta)
  .sort((a, b) => a.id.localeCompare(b.id));

const BASE = import.meta.env.BASE_URL.replace(/\/+$/, "");

/** The one canonical href for a world. */
export function worldHref(slug: string) {
  return `${BASE}/w/${slug}`;
}

function knownSlug(slug: string | undefined | null) {
  return slug && slug in WORLDS ? slug : null;
}

const LEGACY_HASH = /^#\/(?:world|w)\/([a-z0-9-]+)\/?$/;
const WORLD_PATH = /^\/w\/([a-z0-9-]+)\/?$/;

/**
 * Rewrites the wave-1 hash routes onto the canonical path BEFORE anything mounts.
 * `#/world/<slug>` (W1-1) and `#/w/<slug>` (W1-4) both land on `/w/<slug>`.
 * Returns true when it rewrote, so callers know the location changed.
 */
export function redirectLegacyWorldHash(location: Location = window.location): boolean {
  const slug = knownSlug(location.hash.match(LEGACY_HASH)?.[1]);
  if (!slug) return false;
  window.history.replaceState({}, "", worldHref(slug));
  return true;
}

/** The world named by the current URL, or null when this is a Foundry/showcase route. */
export function matchWorldSlug(location: Location = window.location): string | null {
  const path = location.pathname.startsWith(BASE) ? location.pathname.slice(BASE.length) : location.pathname;
  return knownSlug(path.match(WORLD_PATH)?.[1]);
}
